import { TabContext, TabList } from '@mui/lab';
import { InputAdornment, Stack, TextField, Typography } from '@mui/material';

import { useState, useEffect, useMemo } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import {
	ADD_MAPPING_TITLES,
	RELATION_MAPPING_TAB_VALUES,
} from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';
import {
	setAllEntityResponseData,
	setEntityResponseData,
	setOriginalEntityResponseData,
} from 'common/store/reducer/taxonomyReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { IAddMappingModal, ITabValueData } from '../../api/type-def';
import StyledTab from '../../StyledTab/StyledTab';
import {
	fetchAPIBasedOnEntity,
	fetchCurrentEntityData,
	handleSelectAllChange,
	handleSingleOptionChange,
	searchForDataInMapping,
} from '../../TaxonomyUtils';

import AddMappingMenuItem from '../AddMappingMenuItem/AddMappingMenuItem';

import styles from './AddMappingModal.module.scss';

/* Descructuring of variables */
const { ALL, NONE, OBLIGATIONS } = RELATION_MAPPING_TAB_VALUES;
const { MODAL_TITLE } = ADD_MAPPING_TITLES;
const {
	textColor,
	tabBorderRadius,
	tabBackground,
	tabSelectedTextColor,
	focustabColor,
	searchTextHeight,
} = styles;

/* Local Variables */
const style = {
	'.MuiButtonBase-root': {
		border: 'none',
	},
	'.MuiButtonBase-root.MuiTab-root.Mui-selected': {
		color: tabSelectedTextColor,
		backgroundColor: tabBackground,
	},
	'.Mui-selected': {
		background: `${focustabColor} !important`,
		color: textColor,
		borderRadius: tabBorderRadius,
	},
	'.MuiTabs-flexContainer': {
		backgroundColor: `${tabBackground} !important`,
	},
	'.MuiTabs-indicator': {
		display: 'none',
	},
};

const AddMappingModal = (props: IAddMappingModal) => {
	/* Descructuring of props */
	const {
		isModalOpen,
		parentEntityType,
		childEntityOptionsData,
		modalCloseHandler,
		onAddBtnClickHandler,
		moduleType,
		isAddingRelationMapping,
		isAllEntityDataLoading,
		entityId,
	} = props;

	/* Local Variables */
	const dispatch = useDispatch();
	const { subPartId, groupId } = useParams();

	/* Local states */
	const [tabValue, setTabValue] = useState<string>(ALL);
	const [searchText, setSearchText] = useState<string>('');
	const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [isEntityDataLoading, setIsEntityDataLoading] = useState<boolean>(true);

	/* Store Variables */
	const entityResponseData = useSelector(
		(state: RootState) => state.taxonomy.entityResponseData,
	);
	const riskRefTaxonomySourceFiltersKey = useSelector(
		(state: RootState) => state.taxonomy.riskRefTaxonomySourceFiltersKey,
	);

	const entityCountData = useSelector(
		(state: RootState) => state.taxonomy.entityCountData,
	);

	const originalEntityResponseData = useSelector(
		(state: RootState) => state.taxonomy.originalEntityResponseData,
	);

	const allOriginalEntityResponseData = useSelector(
		(state: RootState) => state.taxonomy.allOriginalEntityResponseData,
	);

	const allEntityResponseData = useSelector(
		(state: RootState) => state.taxonomy.allEntityResponseData,
	);

	/* To fetch current entity original data based on tabValue */
	const currentOriginalEntityData = fetchCurrentEntityData(
		tabValue,
		allOriginalEntityResponseData,
		originalEntityResponseData,
	);

	/* To fetch current entity loading status based on tabValue */
	const currentEntityLoadingFlag = fetchCurrentEntityData(
		tabValue,
		isAllEntityDataLoading,
		isEntityDataLoading,
	);

	/* To fetch current entity data based on tabValue */
	const currentEntityResponseData = fetchCurrentEntityData(
		tabValue,
		allEntityResponseData,
		entityResponseData,
	);

	/**
	 * @description method to trigger when tab value of add mapping changes
	 * @param event, newValue
	 */
	const addMappingTabValueChangeHandler = (
		event: React.SyntheticEvent,
		newValue: string,
	) => {
		event.stopPropagation();
		dispatch(setEntityResponseData([]));
		dispatch(setAllEntityResponseData([]));
		setTabValue(newValue);
	};

	/**
	 * @description method to trigger when search value changes
	 * @param event
	 */
	const searchFieldChangeHandler = (event: any) => {
		setSearchText(event?.target?.value);
	};

	/**
	 * @description method to handle change in options
	 * @param option
	 */
	const handleOptionChange = (option: any) => {
		handleSingleOptionChange(selectedOptions, option, setSelectedOptions);
		if (selectedOptions?.length === currentOriginalEntityData?.length) {
			setSelectAll(true);
		} else {
			setSelectAll(false);
		}
	};

	/**
	 * @description method to fetch mapping options from api
	 * @param searchText, tabValue
	 */
	const fetchMappingOptions = async (tabValue: string) => {
		setIsEntityDataLoading(true);
		const sourceParam = riskRefTaxonomySourceFiltersKey || 'PWC';
		const querySourceParam = sourceParam ? { source: sourceParam } : '';
		const queryMappedId = {
			parentId: entityId ?? subPartId ?? groupId,
		};
		const mappingFilter =
			tabValue?.toLowerCase() !== OBLIGATIONS.toLowerCase()
				? { ...querySourceParam, ...queryMappedId }
				: { ...{ searchText: '' }, ...queryMappedId };
		const response = await fetchAPIBasedOnEntity(tabValue, mappingFilter);
		setIsEntityDataLoading(false);
		dispatch(setOriginalEntityResponseData(response));
		dispatch(setEntityResponseData(response));
	};

	/* call api based on tabValue change */
	useEffect(() => {
		if (tabValue?.toLowerCase() !== ALL.toLowerCase()) {
			fetchMappingOptions(tabValue);
		}
	}, [tabValue]);

	/* to check all options */
	useEffect(() => {
		const filteredSelectedOptions = currentEntityResponseData?.filter(
			(data: any) => !data?.mapped,
		);
		const flag =
			filteredSelectedOptions?.every((element: any) =>
				selectedOptions?.some((item) => item?.id === element?.id),
			) &&
			selectedOptions?.every((element) =>
				filteredSelectedOptions?.some((item: any) => item?.id === element?.id),
			);

		setSelectAll(flag);
	}, [selectedOptions, entityResponseData, allEntityResponseData]);

	/* to memoize the search results */
	const entitySearchResult = useMemo(
		() =>
			searchForDataInMapping(currentOriginalEntityData, searchText, tabValue),
		[
			originalEntityResponseData,
			searchText,
			tabValue,
			allOriginalEntityResponseData,
		],
	);

	/* implementing search functionality */
	useEffect(() => {
		if (currentOriginalEntityData && entitySearchResult) {
			if (tabValue?.toLowerCase() === ALL.toLowerCase()) {
				dispatch(setAllEntityResponseData(entitySearchResult));
			} else {
				dispatch(setEntityResponseData(entitySearchResult));
			}
		}
	}, [entitySearchResult]);

	const closeHandler = () => {
		modalCloseHandler(false);
	};

	return (
		<ModalEle
			open={isModalOpen}
			handleClose={closeHandler}
			restrictDefaultCloseAction
			sxBoxProps={{
				width: '50%',
				height: '30%',
				margin: 'auto',
			}}
		>
			<Stack
				className={styles['add-mapping-container']}
				onMouseDown={(event) => event.stopPropagation()}
				onClick={(event) => event.stopPropagation()}
				data-testid="modal-container"
			>
				<Typography className={styles['modal-title']}>
					{toSentenceCase('Add mapping')}
				</Typography>
				<CloseIcon
					fontSize="medium"
					onClick={closeHandler}
					className={`${styles['add-closemodal']} ${
						isAddingRelationMapping ? styles['disable-btn'] : ''
					}`}
					data-testid="close-icon"
				/>
			</Stack>
			<p className={styles['modal-subtitle']} data-testid="modal-subtitle">
				{parentEntityType?.toLowerCase() === NONE.toLowerCase()
					? toSentenceCase('Select items to add to map.')
					: toSentenceCase(
							`${MODAL_TITLE} ${parentEntityType?.toLowerCase()}.`,
					  )}
			</p>
			<TabContext value={tabValue}>
				<TabList
					sx={style}
					onChange={(event: React.SyntheticEvent, value: string) =>
						addMappingTabValueChangeHandler(event, value)
					}
					className={styles['panel-layout']}
				>
					{childEntityOptionsData?.map(
						(parentData: ITabValueData, index: number) => (
							<StyledTab
								key={parentData?.value}
								data-testid="tabs-value"
								label={`${parentData?.label} (${
									entityCountData[index]?.entityCount || 0
								})`}
								value={parentData?.value}
								disabled={currentEntityLoadingFlag}
							/>
						),
					)}
				</TabList>
			</TabContext>
			<TextField
				className={styles['search-field']}
				autoFocus
				defaultValue={searchText}
				name="search-field"
				id="search-field"
				placeholder="Search"
				sx={{
					'& .MuiInputBase-root': {
						height: searchTextHeight,
					},
				}}
				fullWidth
				onChange={(e) => {
					searchFieldChangeHandler(e);
				}}
				InputProps={{
					inputProps: {
						'data-testid': 'search-field',
					},
					startAdornment: (
						<InputAdornment position="start">
							<SearchOutlined className={styles['input-search-icon']} />
						</InputAdornment>
					),
				}}
			/>
			<AddMappingMenuItem
				selectAll={selectAll}
				selectedOptions={selectedOptions}
				tabValue={tabValue}
				isEntityDataLoading={currentEntityLoadingFlag}
				moduleType={moduleType}
				handleOptionChange={handleOptionChange}
				handleSelectAllChange={() =>
					handleSelectAllChange(
						selectAll,
						currentEntityResponseData,
						setSelectedOptions,
						setSelectAll,
					)
				}
				isAddingRelationMapping={isAddingRelationMapping}
			/>
			<Stack className={styles['button-container']}>
				<ButtonComp
					btnType="secondary"
					clickHandler={closeHandler}
					data-testid="cancel-btn"
					disabled={isAddingRelationMapping}
				>
					{toSentenceCase('Cancel')}
				</ButtonComp>
				<ButtonComp
					clickHandler={() => {
						onAddBtnClickHandler(selectedOptions);
					}}
					btnType="primary"
					data-testid="save-btn"
					disabled={!selectedOptions?.length}
					loading={isAddingRelationMapping}
				>
					{toSentenceCase('Add')}
				</ButtonComp>
			</Stack>
		</ModalEle>
	);
};

export default AddMappingModal;
